import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import Guid from "common/values/guid/guid";
import Session from "users/session/session";
import ProposalAPIService from "work/entities/proposal/api/proposal-api-service";
import DetailedWorkProposalAPIResponse from "work/entities/proposal/api/response-contracts/detailed-work-proposal-api-response";
import Proposal from "work/entities/proposal/proposal";
import ProposalRedline from "work/entities/proposal/redlining/proposal-redline";
import ProposalBuilder from "work/entities/proposal/utils/proposal-builder";
import BarebonesWorkAgreementInfoAPIResponse from "work/entities/work-agreement/api/request-contracts/barebones-work-agreement-api-response";
import WorkAgreementParameters from "work/entities/work-agreement/api/request-contracts/work-agreement-parameters";
import WorkAgreementAPIResponse from "work/entities/work-agreement/api/response-contracts/work-agreement-api-response";
import WorkAgreement, { IWorkAgreementStore } from "work/entities/work-agreement/work-agreement";
import WorkFeeScheduleAPIResponse from "work/values/fee-schedule/api/response-contracts/work-fee-schedule-api-response";
import FeeSchedule from "work/values/fee-schedule/fee-schedule";
import DetailedWorkVendorTeamAPIResponse from "work/values/vendor-team/api/response-contracts/detailed-work-vendor-team-api-response";
import VendorTeam from "work/values/vendor-team/vendor-team";
import PaginatedResponse from "../../../../common/contracts/paginated-response";
import { WorkAgreementQuery } from "work/entities/work-agreement/store/work-agreement-redux-slice";

export default class WorkAgreementAPIService implements IWorkAgreementStore {

  private readonly authHeaders: AxiosHeaders = new AxiosHeaders();

  constructor(session: Readonly<Session>) {
    if (!session.authToken?.value)
      throw new Error("Session must have an authToken to create a WorkAgreementAPIService");
    this.authHeaders.set(
      "Authorization",
      `Bearer ${session.authToken.value}`
    );
  }

  async getWorkAgreementsAsync(query: WorkAgreementQuery, abortController?: AbortController): Promise<PaginatedResponse<WorkAgreement>> {
    try {
      const queryString = query.asSearchParams().toString()
      const url = new URL(
        `/work/?${queryString}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      const responseData: WorkAgreementAPIResponse[] = response.data;
      const workAgreements = responseData.map((workAgreementData) => {
          return Object.assign(
            new WorkAgreementAPIResponse(),
            workAgreementData
          ).deserialize(
            this
          )
        }
      );

      const paginationHeader = response.headers['x-pagination'];
      if (!paginationHeader) {
          throw new WorkAgreementAPIServiceError(
              "getWorkAgreementsAsync",
              "Missing x-pagination header"
            );
        }
      return PaginatedResponse.from<WorkAgreement>(workAgreements, paginationHeader);      
    } catch (error) {
      console.error(error);
      if (error instanceof CanceledError)
        throw error;
      throw new WorkAgreementAPIServiceError(
        "getWorkAgreementsAsync",
        error
      );
    }
  }

  async getWorkAgreementByIdAsync(workId: Guid, abortController?: AbortController): Promise<WorkAgreement> {
    try {
      const url = new URL(
        `/work/${workId.value}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      return Object.assign(
        new WorkAgreementAPIResponse(),
        response.data
      ).deserialize(this);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
        throw new InvalidWorkAgreementUserError(error.response?.data?.message, undefined, workId);
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new WorkAgreementNotFoundError(error.response?.data?.message, workId);

      if (error instanceof CanceledError)
        throw error;

      throw new WorkAgreementAPIServiceError("getWorkByIdAsync", error);
    }
  }

  async getWorkAgreementsByIds(
    ids: Guid[],
    abortController?: AbortController
  ): Promise<BarebonesWorkAgreementInfoAPIResponse[]> {
    let url = new URL(
      "/work/work/get-by-ids",
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      const response = await axios.post(
        url.toString(),
        ids.map((id) => id.value),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      return response.data.map((workAgreementData: Partial<BarebonesWorkAgreementInfoAPIResponse>) =>
        Object.assign(
          new BarebonesWorkAgreementInfoAPIResponse(),
          workAgreementData
        )
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest)
        throw new InvalidWorkAgreementRequestError(error.message);
      if (error instanceof CanceledError)
        throw error;
      throw new WorkAgreementAPIServiceError(
        "getWorkAgreementsByIds",
        error
      );
    }
  }

  async getWorkTeamsAsync(workId: Guid, abortController?: AbortController): Promise<VendorTeam[]> {
    try {
      const url = new URL(
        `/work/${workId.value}/teams`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      const responseData: DetailedWorkVendorTeamAPIResponse[] = response.data;
      return responseData.map((teamData: DetailedWorkVendorTeamAPIResponse) => {
        return Object.assign(
          new DetailedWorkVendorTeamAPIResponse(),
          teamData
        ).deserialize() as VendorTeam;
      });
    } catch (error) {
      if (error instanceof CanceledError)
        throw error;
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
        throw new InvalidWorkAgreementUserError(error.response?.data?.message, undefined, workId);
      throw new WorkAgreementAPIServiceError("getTeamsAsync", error);
    }
  }

  async quitWorkTeamAsync(workId: Guid, session: Readonly<Session>): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    try {
      const url = new URL(
        `/work/${workId.value}/teams/quit`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );
      return Object.assign(
        new DetailedWorkProposalAPIResponse(),
        response.data
      ).deserializeDetailed(
        new ProposalAPIService(session, this),
        this,
        session.user,
        true
      );
    } catch (error) {
      throw new WorkAgreementAPIServiceError("quitWorkTeamAsync", error);
    }
  }

  async getWorkFeeSchedulesAsync(
    entityId: Guid,
    representativeType?: string,
    abortController?: AbortController): Promise<FeeSchedule[]> {
    try {
      const url = representativeType ?
        new URL(`/work/${entityId.value}/fee-schedules?representativeType=${representativeType}`,
          AttorneyHubAPIService.apiBaseUrl) :
        new URL(`/work/${entityId.value}/fee-schedules`,
          AttorneyHubAPIService.apiBaseUrl);
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      const responseData: WorkFeeScheduleAPIResponse[] = response.data;
      return responseData.map((feeScheduleData: WorkFeeScheduleAPIResponse) => {
        return Object.assign(
          new WorkFeeScheduleAPIResponse(),
          feeScheduleData
        ).deserialize() as FeeSchedule;
      });
    } catch (error) {
      if (error instanceof CanceledError)
        throw error;
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
        throw new InvalidWorkAgreementUserError(error.response?.data?.message, undefined, entityId);
      throw new WorkAgreementAPIServiceError("getFeeSchedulesAsync", error);
    }
  }

  async reviseWorkAgreementAsync(
    workAgreement: WorkAgreement,
    session: Readonly<Session>
  ): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!workAgreement.id) {
      throw new Error("Cannot revise work agreement without id.");
    }
    try {
      const url = new URL(
        `/work/${workAgreement.id.value}/revise`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        workAgreement.id.value,
        {
          headers: this.authHeaders
        }
      );

      return Object.assign(
        new DetailedWorkProposalAPIResponse(),
        response.data
      ).deserializeDetailed(
        new ProposalAPIService(session, this),
        this,
        session.user,
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
        throw new InvalidWorkAgreementUserError(error.response?.data?.message, undefined, workAgreement.id);
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
        throw new WorkAgreementNotFoundError(error.response?.data?.message, workAgreement.id);
      throw new WorkAgreementAPIServiceError(
        "reviseWorkAgreement",
        error
      );
    }
  }

  async endWorkAgreementAsync(workAgreement: WorkAgreement): Promise<WorkAgreement> {
    if (!workAgreement.id) {
      throw new Error("Cannot end work agreement without id.");
    }

    try {
      const url = new URL(
        `/work/${workAgreement.id.value}/end-agreement`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );

      return Object.assign(
        new WorkAgreementAPIResponse(),
        response.data
      ).deserialize(this);
    } catch (error) {
      throw new WorkAgreementAPIServiceError(
        "endWorkAgreement",
        error
      );
    }
  }
}

export class WorkAgreementAPIServiceError extends Error {
  constructor(method: string, error: unknown) {
    super(`Error calling WorkAgreementAPIService.${method}: Error: ${error}`);
  }
}

export class InvalidWorkAgreementRequestError extends Error {
  constructor(message: string) {
    super(`Invalid Work Agreement API Request: ${message}`);
  }
}
export class WorkAgreementNotFoundError extends Error {
  workId: Guid;
  constructor(message: string, workAgreementId: Guid) {
    super(message);
    this.workId = workAgreementId;
  }
}
export class InvalidWorkAgreementUserError extends Error {
  query?: WorkAgreementParameters;
  workId?: Guid;
  constructor(message: string, query?: WorkAgreementParameters, workId?: Guid) {
    super(message);
    this.query = query;
    this.workId = workId;
  }
}
